import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  alpha,
  Divider,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon as MenuItemIcon,
} from '@mui/material';
import { 
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getTicketsByDateRange, getTicketById } from '../services/ticketDeCaisseService';
import { generateTicketPreview } from '../utils/ticketPreviewUtils';
import TICKET_TYPE, { normalizeTicketType } from '../../../constants/ticketTypes';

const CategoryPanel = ({ 
  categories = [], 
  selectedCategory, 
  onSelectCategory,
  totalProducts = 0,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const debounceRef = useRef(null);

  // Get store info and payment methods from Redux
  const storeInfo = useSelector((state) => state.company?.companyInfo || state.auth?.companyInfo);
  const paymentMethodsFromStore = useSelector((state) => state.paymentMethod.paymentMethods) || [];

  // Today's Tickets state
  const [todayTicketsOpen, setTodayTicketsOpen] = useState(false);
  const [todayTickets, setTodayTickets] = useState([]);
  const [todayTicketsLoading, setTodayTicketsLoading] = useState(false);
  const [todayTicketsPage, setTodayTicketsPage] = useState(0);
  const [todayTicketsPerPage] = useState(10);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuTicket, setActionMenuTicket] = useState(null);

  // Preview Dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value); // update input immediately (no lag)
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCategorySearch(value.toLowerCase().trim());
    }, 250);
  };

  const filteredCategories = useMemo(() => {
    if (!categorySearch) return categories;
    return categories.filter(c =>
      (c.name || '').toLowerCase().includes(categorySearch)
    );
  }, [categories, categorySearch]);

  const handleOpenTodayTickets = async () => {
    setTodayTicketsOpen(true);
    setTodayTicketsLoading(true);
    setTodayTicketsPage(0);
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await getTicketsByDateRange(today, today);
      const tickets = response?.payload || response?.data || response || [];
      setTodayTickets(Array.isArray(tickets) ? tickets : []);
    } catch (error) {

      setTodayTickets([]);
    } finally {
      setTodayTicketsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '€0.00';
    return `€${Number(amount).toFixed(2)}`;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const paginatedTickets = todayTickets.slice(
    todayTicketsPage * todayTicketsPerPage,
    todayTicketsPage * todayTicketsPerPage + todayTicketsPerPage
  );

  const todayTotal = todayTickets.reduce((sum, t) => sum + Number(t.PaidAmount || t.TotalAmountInclVATAfterDiscount || t.TotalAmount || 0), 0);

  const todaySaleCount = todayTickets.filter(t => t.TicketType !== 1 && t.TicketType !== 'RepairTicket').length;
  const todayRepairCount = todayTickets.filter(t => t.TicketType === 1 || t.TicketType === 'RepairTicket').length;

  const handleOpenActionMenu = (event, ticket) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuTicket(ticket);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuTicket(null);
  };

  const handleReprint = async () => {
    if (!actionMenuTicket) return;
    
    handleCloseActionMenu();
    setPreviewLoading(true);
    setPreviewOpen(true);

    try {
      // Fetch full ticket details
      const response = await getTicketById(actionMenuTicket.Id);
      const fullTicket = response?.payload || response?.data?.payload || response;
      
      
      if (!fullTicket || !fullTicket.LineItems) {
        toast.error(t('failed_to_load_ticket') || 'Failed to load ticket');
        setPreviewOpen(false);
        return;
      }

      // Prepare cart data from ticket line items
      const cart = (fullTicket.LineItems || fullTicket.Items || []).map(item => ({
        Id: item.ProductId || item.Id,
        Name: item.ProductName || item.Name || 'Product',
        Price: parseFloat(item.UnitPriceExclTax || item.UnitPrice || item.Price || 0),
        Quantity: parseFloat(item.Quantity || 1),
        VAT: parseFloat(item.VATRate || item.VAT || fullTicket.VATPercentage || 21),
        PriceInclVAT: parseFloat(item.UnitPriceInclVat || item.UnitPriceInclVAT || 0),
        SerialNumber: item.ProductSerial || item.SerialNumber || '',
        Total: parseFloat(item.TotalAmountInclVATAfterDiscount || item.TotalAmountInclVAT || item.TotalPrice || (item.Quantity * (item.UnitPriceInclVat || item.UnitPrice)) || 0)
      }));

      // Prepare customer data
      const customer = fullTicket.Customer || 
        (fullTicket.CustomerId ? { Id: fullTicket.CustomerId, Name: fullTicket.CustomerName || 'Walk-in Customer' } : null);

      const walkInCustomerName = !fullTicket.Customer && !fullTicket.CustomerId ? (fullTicket.CustomerName || '') : '';
      // Prepare payment method - extract as string directly
      let paymentMethodName;
      const paymentDetailsArray = fullTicket.PaymentDetails || fullTicket.SplitPayments || [];
      
      if (paymentDetailsArray.length > 1) {
        // Multiple payments
        const splitNames = paymentDetailsArray.map(sp => {
          const methodName = sp.PaymentMethodName || paymentMethodsFromStore.find(p => p.Id === sp.PaymentMethodId)?.Name || 'N/A';
          return `${methodName} (€${parseFloat(sp.Amount || 0).toFixed(2)})`;
        }).join(', ');
        paymentMethodName = splitNames;
      } else if (paymentDetailsArray.length === 1) {
        // Single payment from PaymentDetails
        paymentMethodName = paymentDetailsArray[0].PaymentMethodName || 'Cash';
      } else {
        // Fallback to ticket-level payment method name
        paymentMethodName = fullTicket.PaymentMethodName || fullTicket.PaymentMethod || 'Cash';
      }


      const paidAmount = parseFloat(fullTicket.PaidAmount || fullTicket.TotalAmountInclVATAfterDiscount || fullTicket.TotalAmount || 0);
      const receiptNumber = fullTicket.ReceiptNumber || fullTicket.TicketNumber;
      
      // Map discount
      const discount = fullTicket.DiscountAmount && fullTicket.DiscountAmount > 0 ? {
        type: fullTicket.DiscountType?.toLowerCase() === 'percentage' ? 'percentage' : 'fixed',
        value: fullTicket.DiscountType?.toLowerCase() === 'percentage' 
          ? parseFloat(fullTicket.DiscountPercentage || 0) 
          : parseFloat(fullTicket.DiscountAmount || 0),
        amount: parseFloat(fullTicket.DiscountAmount || 0),
        reason: fullTicket.DiscountReason || '',
      } : null;
      
      const customVATRate = fullTicket.VATPercentage !== null && fullTicket.VATPercentage !== undefined 
        ? parseFloat(fullTicket.VATPercentage) 
        : null;

      const normalizedType = normalizeTicketType(fullTicket.TicketType);
      const ticketType = normalizedType === TICKET_TYPE.REPAIR ? 'Repair' : 'Sale';
      const pickupDate = fullTicket.PickupDate || null;
      const notes = fullTicket.Notes || undefined;
      // Generate preview HTML
      const receiptHtml = generateTicketPreview({
        cart,
        customer,
        storeInfo,
        ticketType,
        receiptNumber,
        paidAmount,
        paymentMethod: paymentMethodName,
        allPaymentMethods: paymentMethodsFromStore,
        useMultiplePayments: false,
        paymentMethodsArray: [],
        discount,
        customVATRate,
        notes,
        walkInCustomerName,
        pickupDate,
        ticketDate: fullTicket.CreatedDate,
        isTvaIncluded: false,
      });

      setPreviewHtml(receiptHtml);
    } catch (error) {

      toast.error(t('failed_to_generate_preview') || 'Failed to generate preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePrintFromPreview = () => {
    try {
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'absolute';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      
      document.body.appendChild(printFrame);
      
      printFrame.srcdoc = previewHtml;
      
      printFrame.onload = () => {
        setTimeout(() => {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          
          setTimeout(() => {
            document.body.removeChild(printFrame);
            setPreviewOpen(false);
            toast.success(t('print_sent') || 'Print job sent');
          }, 100);
        }, 250);
      };
    } catch (error) {

      toast.error(t('print_failed') || 'Failed to print');
    }
  };

  const handleConvertToInvoice = async () => {
    if (!actionMenuTicket) return;
    
    handleCloseActionMenu();
    setTodayTicketsOpen(false);

    try {
      toast.info(t('loading_ticket_data') || 'Loading ticket data...');
      
      // Fetch full ticket details
      const response = await getTicketById(actionMenuTicket.Id);
      const fullTicket = response?.payload || response?.data?.payload || response;
      
      
      if (!fullTicket || !fullTicket.LineItems) {
        toast.error(t('failed_to_load_ticket') || 'Failed to load ticket');
        return;
      }

      // Map products from ticket to invoice format
      const products = (fullTicket.LineItems || fullTicket.Items || []).map(item => ({
        id: item.ProductId || item.Id,
        name: item.ProductName || item.Name || 'Product',
        quantity: parseFloat(item.Quantity || 1),
        unitPrice: parseFloat(item.UnitPriceExclTax || item.UnitPrice || item.Price || 0),
        unitPriceInclVAT: parseFloat(item.UnitPriceInclVat || item.UnitPriceInclVAT || 0),
        vat: parseFloat(item.VATRate || item.VAT || fullTicket.VATPercentage || 21),
        totalPrice: parseFloat(item.TotalAmountInclVATAfterDiscount || item.TotalAmountInclVAT || item.TotalPrice || 0),
        serialNumber: item.ProductSerial || item.SerialNumber || '',
        discount: item.DiscountAmount ? parseFloat(item.DiscountAmount) : 0,
      }));

      // Map customer
      let customer = null;
      let customerSearchName = '';
      
      if (fullTicket.Customer) {
        customer = {
          Id: fullTicket.Customer.Id,
          Name: fullTicket.Customer.Name || fullTicket.Customer.CompanyName,
          CompanyName: fullTicket.Customer.CompanyName,
          Email: fullTicket.Customer.Email || '',
          PhoneNumber: fullTicket.Customer.Phone || fullTicket.Customer.PhoneNumber || '',
          VATNumber: fullTicket.Customer.VATNumber || '',
          Street: fullTicket.Customer.Street || '',
          City: fullTicket.Customer.City || '',
          PostCode: fullTicket.Customer.PostCode || '',
          Country: fullTicket.Customer.Country || '',
        };
      } else if (fullTicket.CustomerId) {
        customer = {
          Id: fullTicket.CustomerId,
          Name: fullTicket.CustomerName || 'Customer',
        };
      } else if (fullTicket.CustomerName) {
        customerSearchName = fullTicket.CustomerName;
      }

      // Map discount
      let discount = null;
      if (fullTicket.DiscountAmount && fullTicket.DiscountAmount > 0) {
        discount = {
          type: fullTicket.DiscountType?.toLowerCase() === 'percentage' ? 'percentage' : 'fixed',
          value: fullTicket.DiscountType?.toLowerCase() === 'percentage'
            ? parseFloat(fullTicket.DiscountPercentage || 0)
            : parseFloat(fullTicket.DiscountAmount || 0),
        };
      }

      // Map payment method
      let useMultiplePayments = false;
      let paymentMethodsArray = [];
      let paymentMethodId = null;
      let paymentMethodName = null;

      const paymentDetailsArray = fullTicket.PaymentDetails || fullTicket.SplitPayments || [];
      
      if (paymentDetailsArray.length > 1) {
        // Multiple payments
        useMultiplePayments = true;
        paymentMethodsArray = paymentDetailsArray.map(sp => {
          const methodName = sp.PaymentMethodName || paymentMethodsFromStore.find(p => p.Id === sp.PaymentMethodId)?.Name || 'Unknown';
          return {
            id: sp.PaymentMethodId,
            name: methodName,
            amount: parseFloat(sp.Amount || 0),
          };
        });
      } else if (paymentDetailsArray.length === 1) {
        // Single payment from PaymentDetails
        const payment = paymentDetailsArray[0];
        paymentMethodId = payment.PaymentMethodId;
        paymentMethodName = payment.PaymentMethodName;
      } else if (fullTicket.PaymentMethodId) {
        // Fallback to ticket-level payment method ID
        paymentMethodId = fullTicket.PaymentMethodId;
      } else if (fullTicket.PaymentMethodName) {
        // Fallback to ticket-level payment method name
        paymentMethodName = fullTicket.PaymentMethodName;
      }

      // Navigate to add invoice page with mapped data
      navigate('/invoices/add', {
        state: {
          fromTicket: {
            ticketNumber: fullTicket.TicketNumber || fullTicket.ReceiptNumber,
            products,
            customer,
            customerSearchName,
            discount,
            vatRate: fullTicket.VATPercentage,
            isTvaIncluded: false,
            notes: fullTicket.Notes || '',
            paidAmount: parseFloat(fullTicket.PaidAmount || 0),
            useMultiplePayments,
            paymentMethodsArray,
            paymentMethodId,
            paymentMethodName,
          }
        }
      });

      toast.success(t('converting_to_invoice') || 'Converting ticket to invoice...');
    } catch (error) {

      toast.error(t('failed_to_convert') || 'Failed to convert ticket to invoice');
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: { xs: 280, sm: 280, md: 260 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
        borderRight: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '2px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          {t('categories') || 'Categories'} ({categories.length})
        </Typography>
      </Box>

      {/* Today's Tickets Button */}
      <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ReceiptIcon sx={{ fontSize: 16 }} />}
          onClick={handleOpenTodayTickets}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.82rem',
            borderRadius: 1.5,
            py: 0.75,
            borderColor: '#667eea',
            color: '#667eea',
            bgcolor: alpha('#667eea', 0.04),
            '&:hover': {
              borderColor: '#764ba2',
              bgcolor: alpha('#667eea', 0.1),
            },
          }}
        >
          {t('todays_tickets') || "Today's Tickets"}
        </Button>
      </Box>

      {/* Category Search */}
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('search_categories') || 'Search categories...'}
          value={searchInput}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              fontSize: '0.82rem',
              bgcolor: 'white',
            },
            '& .MuiInputBase-input': { py: 0.75 },
          }}
        />
      </Box>

      {/* Categories List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: alpha('#000', 0.05),
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: alpha('#667eea', 0.3),
            borderRadius: '3px',
            '&:hover': {
              bgcolor: alpha('#667eea', 0.5),
            },
          },
        }}
      >
        {filteredCategories.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {categorySearch ? (t('no_categories_match') || 'No categories match') : (t('no_categories') || 'No categories available')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {filteredCategories.map((category, index) => {
              const isSelected = selectedCategory?.id === category.id;
              
              return (
                <React.Fragment key={category.id}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => onSelectCategory(category)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      transition: 'all 0.2s ease',
                      borderLeft: '4px solid',
                      borderLeftColor: isSelected ? '#667eea' : 'transparent',
                      bgcolor: isSelected ? alpha('#667eea', 0.08) : 'transparent',
                      '&:hover': {
                        bgcolor: isSelected 
                          ? alpha('#667eea', 0.12)
                          : alpha('#667eea', 0.04),
                        borderLeftColor: '#667eea',
                      },
                      '&.Mui-selected': {
                        bgcolor: alpha('#667eea', 0.08),
                        '&:hover': {
                          bgcolor: alpha('#667eea', 0.12),
                        },
                      },
                    }}
                  >
                    {/* Category Icon */}
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          fontSize: '1.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: '8px',
                          bgcolor: isSelected 
                            ? alpha('#667eea', 0.15)
                            : alpha('#667eea', 0.05),
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {category.icon || '📦'}
                      </Box>
                    </ListItemIcon>

                    {/* Category Name */}
                    <ListItemText
                      primary={category.name}
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: isSelected ? 700 : 600,
                          fontSize: '0.9rem',
                          color: isSelected ? '#667eea' : 'text.primary',
                        },
                      }}
                    />

                    {/* Product Count Badge */}
                    <Badge
                      badgeContent={category.count || 0}
                      color="primary"
                      sx={{
                        mr: 1,
                        '& .MuiBadge-badge': {
                          bgcolor: isSelected ? '#667eea' : alpha('#667eea', 0.7),
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          minWidth: 22,
                          height: 22,
                          borderRadius: '11px',
                        },
                      }}
                    />
                  </ListItemButton>
                  
                  {/* Divider between items (except last item) */}
                  {index < filteredCategories.length - 1 && (
                    <Divider sx={{ mx: 2 }} />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      {/* Footer Info */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha('#667eea', 0.03),
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
          {selectedCategory?.name && (
            <>
              <strong>{selectedCategory.name}</strong>
              <br />
              {selectedCategory.count || 0} {t('products') || 'products'}
            </>
          )}
        </Typography>
      </Box>

      {/* Today's Tickets Dialog */}
      <Dialog
        open={todayTicketsOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') setTodayTicketsOpen(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 1.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                {t('todays_tickets') || "Today's Tickets"}
              </Typography>
              {!todayTicketsLoading && (
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {todayTickets.length} {t('tickets') || 'tickets'} • {t('total') || 'Total'}: {formatCurrency(todayTotal)}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={() => setTodayTicketsOpen(false)} size="small" sx={{ color: 'white' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {todayTicketsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : todayTickets.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                {t('no_tickets_today') || 'No tickets today'}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Summary Bar */}
              <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1.5, bgcolor: alpha('#667eea', 0.04), borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                <Chip
                  label={`${t('total_tickets') || 'Total'}: ${todayTickets.length}`}
                  size="small"
                  sx={{ fontWeight: 700, bgcolor: alpha('#667eea', 0.1), color: '#667eea' }}
                />
                <Chip
                  label={`${t('sales') || 'Sales'}: ${todaySaleCount}`}
                  size="small"
                  sx={{ fontWeight: 700, bgcolor: alpha('#4caf50', 0.1), color: '#2e7d32' }}
                />
                <Chip
                  label={`${t('repairs') || 'Repairs'}: ${todayRepairCount}`}
                  size="small"
                  sx={{ fontWeight: 700, bgcolor: alpha('#9c27b0', 0.1), color: '#7b1fa2' }}
                />
                <Chip
                  label={`${t('total_amount') || 'Total Amount'}: ${formatCurrency(todayTotal)}`}
                  size="small"
                  sx={{ fontWeight: 700, bgcolor: alpha('#667eea', 0.15), color: '#667eea', ml: 'auto' }}
                />
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{t('ticket_number') || 'Ticket #'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{t('time') || 'Time'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{t('customer') || 'Customer'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{t('type') || 'Type'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{t('payment') || 'Payment'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }} align="right">{t('amount') || 'Amount'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }} align="center">{t('actions') || 'Actions'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedTickets.map((ticket) => (
                      <TableRow key={ticket.Id} hover sx={{ '&:hover': { bgcolor: alpha('#667eea', 0.04) } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#667eea' }}>
                            {ticket.TicketNumber || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatTime(ticket.CreatedDate || ticket.Date)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ticket.CustomerName || t('walk_in') || 'Walk-in'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ticket.TicketType === 1 || ticket.TicketType === 'RepairTicket' ? t('repair') || 'Repair' : t('sale') || 'Sale'}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 22,
                              bgcolor: ticket.TicketType === 1 || ticket.TicketType === 'RepairTicket' ? alpha('#9c27b0', 0.1) : alpha('#4caf50', 0.1),
                              color: ticket.TicketType === 1 || ticket.TicketType === 'RepairTicket' ? '#7b1fa2' : '#2e7d32',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {ticket.PaymentMethodName || ticket.PaymentMethod || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatCurrency(ticket.PaidAmount || ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenActionMenu(e, ticket)}
                            sx={{ color: '#667eea' }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {todayTickets.length > todayTicketsPerPage && (
                <TablePagination
                  component="div"
                  count={todayTickets.length}
                  page={todayTicketsPage}
                  onPageChange={(_, newPage) => setTodayTicketsPage(newPage)}
                  rowsPerPage={todayTicketsPerPage}
                  rowsPerPageOptions={[10]}
                  sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Menu for Today's Tickets */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        PaperProps={{ sx: { borderRadius: 1.5, minWidth: 180, boxShadow: 3 } }}
      >
        <MenuItem onClick={() => { handleCloseActionMenu(); navigate(`/tickets/view/${actionMenuTicket?.Id}`); setTodayTicketsOpen(false); }}>
          <MenuItemIcon sx={{ minWidth: 32 }}><VisibilityIcon fontSize="small" sx={{ color: '#667eea' }} /></MenuItemIcon>
          {t('detail') || 'Detail'}
        </MenuItem>
        <MenuItem onClick={() => { handleCloseActionMenu(); navigate(`/tickets/edit/${actionMenuTicket?.Id}`); setTodayTicketsOpen(false); }}>
          <MenuItemIcon sx={{ minWidth: 32 }}><EditIcon fontSize="small" sx={{ color: '#ff9800' }} /></MenuItemIcon>
          {t('edit') || 'Edit'}
        </MenuItem>
        <MenuItem onClick={handleReprint}>
          <MenuItemIcon sx={{ minWidth: 32 }}><PrintIcon fontSize="small" sx={{ color: '#4caf50' }} /></MenuItemIcon>
          {t('reprint') || 'Reprint'}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleConvertToInvoice}>
          <MenuItemIcon sx={{ minWidth: 32 }}><DescriptionIcon fontSize="small" sx={{ color: '#1976d2' }} /></MenuItemIcon>
          {t('convert_to_invoice') || 'Convert to Invoice'}
        </MenuItem>
      </Menu>

      {/* Preview Dialog for Reprint */}
      <Dialog
        open={previewOpen}
        onClose={() => !previewLoading && setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 1.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
              {t('ticket_preview') || 'Ticket Preview'}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setPreviewOpen(false)} 
            size="small" 
            sx={{ color: 'white' }}
            disabled={previewLoading}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: '#f5f5f5' }}>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ 
              maxHeight: '70vh', 
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'center',
              p: 2,
            }}>
              <iframe
                srcDoc={previewHtml}
                title="Ticket Preview"
                style={{
                  width: '80mm',
                  minHeight: '400px',
                  border: 'none',
                  backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={() => setPreviewOpen(false)} 
            variant="outlined"
            disabled={previewLoading}
          >
            {t('close') || 'Close'}
          </Button>
          <Button 
            onClick={handlePrintFromPreview}
            variant="contained"
            startIcon={<PrintIcon />}
            disabled={previewLoading}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              }
            }}
          >
            {t('print') || 'Print'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CategoryPanel;